import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppointmentActivity,
  LogAppointmentActivityRequestActionEnum,
  LogAppointmentActivityRequestActivityTypeEnum,
} from "appointment-experience-client";
import { logAppointmentActivity } from "../api-helpers/AppointmentApiHelper";
import {
  AppointmentActivityAction,
  AppointmentActivityType,
  toVisitActivity,
} from "../constants/AppointmentActivity";
import { RootState } from "../store";
import { visitActivityLogged } from "../store/slices/appointmentOverlaySlice";
import { showToast } from "../store/slices/toastSlice";

export interface AppointmentActivityEntry {
  patientId: string;
  activityType: AppointmentActivityType;
  refId: string;
  action: AppointmentActivityAction;
  title?: string;
  metadata?: Record<string, unknown>;
}

/** Waits before each retry of a failed write. */
export const ACTIVITY_LOG_RETRY_DELAYS_MS = [500, 1500];

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/** A 4xx won't succeed on a retry; network errors and 5xx might. */
const isRetryable = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status === undefined || status >= 500;
};

export const useAppointmentActivityLogger = () => {
  const dispatch = useDispatch();
  const appointmentId = useSelector(
    (state: RootState) => state.appointmentOverlay.appointmentId
  );
  const overlayPatientId = useSelector(
    (state: RootState) => state.appointmentOverlay.patientId
  );

  return useCallback(
    async (entry: AppointmentActivityEntry): Promise<void> => {
      if (!appointmentId || !entry.refId || entry.patientId !== overlayPatientId) {
        return;
      }

      const request = {
        ...entry,
        activityType:
          entry.activityType as LogAppointmentActivityRequestActivityTypeEnum,
        action: entry.action as LogAppointmentActivityRequestActionEnum,
      };

      let logged: AppointmentActivity | undefined;
      for (let attempt = 0, written = false; !written; attempt++) {
        try {
          logged = await logAppointmentActivity(appointmentId, request);
          written = true;
        } catch (error) {
          if (
            attempt < ACTIVITY_LOG_RETRY_DELAYS_MS.length &&
            isRetryable(error)
          ) {
            await delay(ACTIVITY_LOG_RETRY_DELAYS_MS[attempt]);
            continue;
          }
          console.error("Error logging appointment activity:", error);
          dispatch(
            showToast({
              type: "warning",
              title: "Couldn't record this in the appointment activity log",
              message: "",
              icon: "warning",
            })
          );
          return;
        }
      }
      // Outside the retry loop: the write has landed, so it must never be resent.
      if (logged) {
        dispatch(visitActivityLogged(toVisitActivity(logged)));
      }
    },
    [appointmentId, overlayPatientId, dispatch]
  );
};
